import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import "../css/productdetails.scss";

const formatPrice = (price, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(Number(price));

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useContext(CartContext);
  const product = useMemo(
    () => productData.find((item) => item.id === Number(id)),
    [id]
  );

  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    setSelectedSize(product?.size?.[0] || "");
    setSelectedColor(product?.colors?.[0] || "");
    setSelectedImage(0);
    setQuantity(1);
  }, [product]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!product) {
    return (
      <main className="product-detail product-detail--not-found">
        <div className="product-detail__not-found">
          <p className="product-detail__eyebrow">ioomparade</p>
          <h1>Product not found</h1>
          <p>The item you are looking for may have moved or is no longer available.</p>
          <Link onClick={scrollToTop} to="/shop">
            Back to the collection
          </Link>
        </div>
      </main>
    );
  }

  const images = product.images?.length ? product.images : [product.image].filter(Boolean);
  const features = product.Features || [];
  const description = Array.isArray(product.description)
    ? product.description
    : [product.description].filter(Boolean);

  const handleAddToCart = () => {
    if (!product.available) return;

    addToCart(product.id, quantity, selectedSize, selectedColor);
    navigate("/cartpage");
    scrollToTop();
  };

  const updateQuantity = (nextQuantity) => {
    const numericQuantity = Number(nextQuantity);
    setQuantity(
      Number.isFinite(numericQuantity) ? Math.max(1, Math.floor(numericQuantity)) : 1
    );
  };

  return (
    <main className="product-detail">
      <nav className="product-breadcrumbs" aria-label="Breadcrumb">
        <Link to="/" onClick={scrollToTop}>
          Home
        </Link>
        <span aria-hidden="true">/</span>
        <Link to="/shop" onClick={scrollToTop}>
          New stationery
        </Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{product.name}</span>
      </nav>

      <div className="product-detail__container">
        <section className="product-gallery" aria-label={`${product.name} image gallery`}>
          <div className="product-gallery__thumbs" aria-label="Choose a product image">
            {images.map((image, index) => (
              <button
                key={image}
                type="button"
                className={`product-gallery__thumb ${
                  index === selectedImage ? "is-active" : ""
                }`}
                onClick={() => setSelectedImage(index)}
                aria-label={`Show image ${index + 1} of ${images.length}`}
                aria-pressed={index === selectedImage}
              >
                <img src={image} alt="" aria-hidden="true" />
              </button>
            ))}
          </div>

          <div className="product-gallery__main">
            <img
              src={images[selectedImage]}
              alt={`${product.name}, view ${selectedImage + 1} of ${images.length}`}
            />
          </div>
        </section>

        <section className="product-summary" aria-labelledby="product-title">
          <p className="product-summary__brand">{product.brand}</p>
          <h1 id="product-title">{product.name}</h1>
          <p className="product-summary__price">
            {formatPrice(product.price, product.currency)}
          </p>

          <div
            className={`product-summary__availability ${
              product.available ? "is-available" : "is-unavailable"
            }`}
          >
            <span aria-hidden="true" />
            {product.available ? "In stock and ready to ship" : "Currently unavailable"}
          </div>

          <div className="product-summary__description">
            {description.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          <div className="product-options">
            {product.colors?.length > 0 && (
              <div className="product-field">
                <label htmlFor="product-color">Design / Color</label>
                <select
                  id="product-color"
                  value={selectedColor}
                  onChange={(event) => setSelectedColor(event.target.value)}
                >
                  {product.colors.map((color) => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {product.size?.length > 0 && (
              <div className="product-field">
                <label htmlFor="product-size">Size</label>
                <select
                  id="product-size"
                  value={selectedSize}
                  onChange={(event) => setSelectedSize(event.target.value)}
                >
                  {product.size.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="product-field product-field--quantity">
              <label htmlFor="product-quantity">Quantity</label>
              <div className="quantity-picker">
                <button
                  type="button"
                  onClick={() => updateQuantity(quantity - 1)}
                  disabled={quantity <= 1}
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <input
                  id="product-quantity"
                  type="number"
                  min="1"
                  inputMode="numeric"
                  value={quantity}
                  onChange={(event) => updateQuantity(event.target.value)}
                  aria-label="Product quantity"
                />
                <button
                  type="button"
                  onClick={() => updateQuantity(quantity + 1)}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="product-summary__add"
            onClick={handleAddToCart}
            disabled={!product.available}
          >
            {product.available ? "Add to cart" : "Sold out"}
          </button>

          <p className="product-summary__shipping-note">
            Shipping and taxes are calculated at checkout.
          </p>

          <div className="product-accordions">
            <details open>
              <summary>Product details</summary>
              <dl className="product-facts">
                {product.sku && (
                  <div>
                    <dt>SKU</dt>
                    <dd>{product.sku}</dd>
                  </div>
                )}
                {product.category && (
                  <div>
                    <dt>Category</dt>
                    <dd>{product.category}</dd>
                  </div>
                )}
                {product.material && (
                  <div>
                    <dt>Material</dt>
                    <dd>{product.material}</dd>
                  </div>
                )}
              </dl>
              {features.length > 0 && (
                <ul>
                  {features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>
              )}
            </details>

            <details>
              <summary>Shipping &amp; returns</summary>
              <p>
                Review our <Link to="/shipping">shipping information</Link> and{" "}
                <Link to="/returns">returns policy</Link> before placing your order.
              </p>
            </details>
          </div>
        </section>
      </div>
    </main>
  );
};

export default ProductDetail;
